import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AnalyticsProvider from "./components/AnalyticsProvider";

// Eager — home is the LCP target.
import Home from "./pages/Home";

// All other routes lazy-load — each becomes a separate chunk.
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const RequestService = lazy(() => import("./pages/RequestService"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const StaffDashboard = lazy(() => import("./pages/StaffDashboard"));
const AdminOverview = lazy(() => import("./pages/AdminOverview"));
const AdminTickets = lazy(() => import("./pages/AdminTickets"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminStaff = lazy(() => import("./pages/AdminStaff"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminFeed = lazy(() => import("./pages/AdminFeed"));
const AdminWhatsApp = lazy(() => import("./pages/AdminWhatsApp"));
const AdminCRM = lazy(() => import("./pages/AdminCRM"));
const SchemeDetail = lazy(() => import("./pages/SchemeDetail"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminInsights = lazy(() => import("./pages/AdminInsights"));
const ImageEditor = lazy(() => import("./pages/ImageEditor"));
const Pricing = lazy(() => import("./pages/Pricing"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const PvcPrint = lazy(() => import("./pages/PvcPrint"));
const VaultShare = lazy(() => import("./pages/VaultShare"));
const TrackTicket = lazy(() => import("./pages/TrackTicket"));

const ToolsHub = lazy(() => import("./pages/ToolsHub"));
const PassportSignatureTool = lazy(() => import("./pages/tools/PassportSignatureTool"));
const CompressTool = lazy(() => import("./pages/tools/CompressTool"));
const ResizeTool = lazy(() => import("./pages/tools/ResizeTool"));
const AnnotateTool = lazy(() => import("./pages/tools/AnnotateTool"));
const TransformTool = lazy(() => import("./pages/tools/TransformTool"));
const WatermarkTool = lazy(() => import("./pages/tools/WatermarkTool"));
const PdfTool = lazy(() => import("./pages/tools/PdfTool"));

function RouteFallback() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#0A101D]">
      <div className="text-center">
        <div className="inline-block h-8 w-8 rounded-full border-2 border-cyan-400/30 border-t-cyan-400 animate-spin" />
        <div className="mt-3 text-xs uppercase tracking-[0.18em] text-white/55 font-bold">Loading…</div>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AnalyticsProvider>
          <Toaster theme="dark" position="top-right" richColors closeButton />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/request" element={<RequestService />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/track" element={<TrackTicket />} />
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              <Route path="/agreement" element={<LegalPage kind="agreement" />} />
              <Route path="/privacy" element={<LegalPage kind="privacy" />} />
              <Route path="/refund" element={<LegalPage kind="refund" />} />

              {/* Tools Hub + tools */}
              <Route path="/tools" element={<ToolsHub />} />
              <Route path="/tools/image-editor" element={<ImageEditor />} />
              <Route path="/tools/passport" element={<PassportSignatureTool kind="passport" />} />
              <Route path="/tools/signature" element={<PassportSignatureTool kind="signature" />} />
              <Route path="/tools/compress" element={<CompressTool mode="reduce" />} />
              <Route path="/tools/increase" element={<CompressTool mode="increase" />} />
              <Route path="/tools/resize" element={<ResizeTool />} />
              <Route path="/tools/annotate" element={<AnnotateTool />} />
              <Route path="/tools/transform" element={<TransformTool />} />
              <Route path="/tools/watermark" element={<WatermarkTool />} />
              <Route path="/tools/img-to-pdf" element={<PdfTool kind="img-to-pdf" />} />
              <Route path="/tools/pdf-to-img" element={<PdfTool kind="pdf-to-img" />} />
              <Route path="/tools/merge-pdf" element={<PdfTool kind="merge-pdf" />} />
              <Route path="/tools/compress-pdf" element={<PdfTool kind="compress-pdf" />} />

              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* User dashboard */}
              <Route path="/dashboard" element={
                <ProtectedRoute roles={["user", "admin"]}><UserDashboard /></ProtectedRoute>
              } />
              <Route path="/pvc-print" element={
                <ProtectedRoute roles={["user", "admin", "staff"]}><PvcPrint /></ProtectedRoute>
              } />
              <Route path="/vault/share/:token" element={<VaultShare />} />

              {/* Staff */}
              <Route path="/staff" element={
                <ProtectedRoute roles={["staff", "admin"]}><StaffDashboard /></ProtectedRoute>
              } />

              {/* Admin */}
              <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminOverview /></ProtectedRoute>} />
              <Route path="/admin/tickets" element={<ProtectedRoute roles={["admin"]}><AdminTickets /></ProtectedRoute>} />
              <Route path="/admin/orders" element={<ProtectedRoute roles={["admin"]}><AdminOrders /></ProtectedRoute>} />
              <Route path="/admin/leads" element={<ProtectedRoute roles={["admin"]}><AdminLeads /></ProtectedRoute>} />
              <Route path="/admin/feed" element={<ProtectedRoute roles={["admin"]}><AdminFeed /></ProtectedRoute>} />
              <Route path="/admin/whatsapp" element={<ProtectedRoute roles={["admin"]}><AdminWhatsApp /></ProtectedRoute>} />
              <Route path="/admin/crm" element={<ProtectedRoute roles={["admin"]}><AdminCRM /></ProtectedRoute>} />
              <Route path="/scheme/:id" element={<SchemeDetail />} />
              <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/staff" element={<ProtectedRoute roles={["admin"]}><AdminStaff /></ProtectedRoute>} />
              <Route path="/admin/analytics" element={<ProtectedRoute roles={["admin"]}><AdminAnalytics /></ProtectedRoute>} />
              <Route path="/admin/insights" element={<ProtectedRoute roles={["admin"]}><AdminInsights /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AnalyticsProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
